import { useMemo, useState, useCallback, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Typo, Button, Dialogue, Spinner } from '@geberit/gdds';

// styles
import styles from './module-box.module.scss';

// types
import type { ModuleBoxProps, FormStatus } from './types';

// components
import { Icon } from 'components/Icon/Icon';
import { InnerHtml } from 'components/InnerHtml/inner-html';
import { FormController } from 'components/ContentElements/Form/form-controller';
import { Headline } from 'components/ContentElementsGdds/headline/headline';

// utils
import { buildDialogueProps } from './utils';
import { useCustomTool } from 'utils/hooks/use-custom-tool';
import { classNameBuilder } from 'utils/classNameBuilder';
import { previewId } from 'utils/preview-id';
import { decodingContent } from 'utils/decodingContent';
import { useIsMobile } from 'components/App/SizeProvider';
import { useXy } from 'utils/hooks/use-xy';

export function ModuleBox({
  headline,
  text,
  icon,
  buttonText,
  type,
  url,
  urlType,
  forms,
  window: windowTarget,
  identifier,
  scriptUrl,
  styleUrl,
  itemIndex,
  itemsSize = 0,
  isChild = false,
}: Readonly<ModuleBoxProps>) {
  const isMobile = useIsMobile();
  const [modalOpen, setModalOpen] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [formStatus, setFormStatus] = useState<FormStatus>({
    status: {},
    submitMessages: {},
  });
  const renderTool = useCustomTool(modalOpen, identifier, scriptUrl, styleUrl);
  const router = useRouter();
  const isXy = useXy();

  const overlaySize = isMobile ? 'xl' : 'l';

  const handleModuleClick = useCallback(
    (e) => {
      switch (type) {
        case 'nordics_lead_module_link':
          if (urlType === 'internal' && windowTarget === '_self') {
            // internal link in same window
            router.push(url);
          } else if (urlType === 'mailto') {
            // email link
            window.open(`mailto:${url}`, '_self');
          } else {
            // external link or internal link in new window
            window.open(url, windowTarget);
          }
          break;
        case 'nordics_lead_module_iframe':
        case 'nordics_lead_module_form':
        case 'nordics_lead_module_script':
          setModalOpen(true);
          break;
        default:
          e.preventDefault();
      }
    },
    [type, url, windowTarget],
  );

  function renderDialogue() {
    const dialogProps = buildDialogueProps(formStatus, overlaySize);

    if (dialogProps.type !== 'custom') {
      return (
        <div className={styles.form_submission_feedback}>
          <Dialogue
            {...dialogProps}
            body={<p>{dialogProps.bodyContent}</p>}
            onClose={() => {
              setFormStatus({ status: {}, submitMessages: {} });
              setModalOpen(false);
            }}
          />
        </div>
      );
    }

    return (
      <div className={styles.form_wrapper}>
        <Dialogue
          {...dialogProps}
          body={[
            type === 'nordics_lead_module_form' && (
              <FormController
                {...forms?.[0]}
                onFormStatusChange={setFormStatus}
                resetFormStatus
                formType="leadmodule"
                border="disable-border"
              />
            ),
            type === 'nordics_lead_module_iframe' && (
              <>
                {!iframeLoaded && (
                  <div className={styles.spinnerWrapper}>
                    <Spinner />
                  </div>
                )}
                <iframe src={url} title={headline} onLoad={() => setIframeLoaded(true)} />
              </>
            ),
            type === 'nordics_lead_module_script' && <>{renderTool()}</>,
          ]}
          onClose={() => setModalOpen(false)}
          showCloseBtn
          size="l"
          type="custom"
        />
      </div>
    );
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps -- we only want to rerender when overlaySize changes
  const memoisedDialogue = useMemo(renderDialogue, [overlaySize]);

  function renderOverlay() {
    if (!modalOpen) {
      return null;
    }

    return (
      <div
        className={classNameBuilder(
          styles.overlay,
          type === 'nordics_lead_module_iframe' && styles.overlayIframe,
        )}
      >
        {memoisedDialogue}
      </div>
    );
  }

  useEffect(() => {
    if (modalOpen) {
      const originalValue = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.overflow = originalValue;
      };
    }

    return () => {};
  }, [modalOpen]);

  const headlineTagsVariant = isXy
    ? isChild
      ? { h4: 'h5', h5: 'h5' }
      : { h4: 'h3', h5: 'h3' }
    : undefined;

  return (
    <>
      <div {...previewId(`#${itemIndex}`)} className={styles.box}>
        <div className={styles.iconBox}>
          <Icon symbol={icon} />
        </div>
        <div className={styles.contentBox}>
          {headline && (
            <Headline
              titlePreviewId="#st_headline"
              tag={isXy ? 'h3' : 'h4'}
              variant="h6"
              title={decodingContent(headline)}
            />
          )}
          <div className={styles.justifyContent}>
            <Typo variant="p2" as="div">
              <InnerHtml
                previewId="#st_text"
                content={text}
                as="p"
                headlineTagsVariant={headlineTagsVariant}
              />
            </Typo>
            <Button
              {...previewId('#st_buttonText')}
              stretched
              onClick={handleModuleClick}
              stylingType={itemsSize === 1 ? 'primary' : undefined}
            >
              {buttonText}
            </Button>
          </div>
        </div>
      </div>
      {renderOverlay()}
    </>
  );
}
